import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink } from "lucide-react";

interface AITool {
  id: string;
  name: string;
  description: string;
  use_case_tags: string[];
  external_link: string;
  logo_url?: string;
}

const AIToolsPage = () => {
  const [tools, setTools] = useState<AITool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");
  const [useCaseFilter, setUseCaseFilter] = useState("");

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("ai_tools")
        .select("*")
        .order("created_at", { ascending: false });

      if (data) setTools(data);
    } catch (error) {
      console.error("Error loading AI tools:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = tools.filter((tool) => {
    const matchesSearch = searchFilter 
      ? tool.name.toLowerCase().includes(searchFilter.toLowerCase()) || 
        tool.description.toLowerCase().includes(searchFilter.toLowerCase())
      : true;
    
    const matchesUseCase = useCaseFilter 
      ? tool.use_case_tags.some(tag => tag.toLowerCase().includes(useCaseFilter.toLowerCase()))
      : true;
    
    return matchesSearch && matchesUseCase;
  });

  const uniqueUseCases = [...new Set(tools.flatMap(tool => tool.use_case_tags))];

  return (
    <Layout>
      <div className="py-8 md:py-12">
        <div className="container-modern">
          <div className="text-center mb-8 md:mb-12 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">AI Tools Directory</h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover and explore the best AI tools for your specific use cases and workflows.
            </p>
          </div>


          {/* Quick Filter Tags */}
          <div className="flex flex-wrap gap-2 mb-6 md:mb-8 px-4">
            <Button
              variant={searchFilter === "" && useCaseFilter === "" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSearchFilter("");
                setUseCaseFilter("");
              }}
            >
              All Tools
            </Button>
            {uniqueUseCases.slice(0, 6).map((useCase) => (
              <Button
                key={useCase}
                variant={useCaseFilter === useCase ? "default" : "outline"}
                size="sm"
                onClick={() => setUseCaseFilter(useCase)}
              >
                {useCase}
              </Button>
            ))}
          </div>

          <div className="mb-4 px-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredTools.length} of {tools.length} tools
            </p>
          </div>

          {/* Tools Grid */}
          {loading ? (
            <Card className="p-6 md:p-8 text-center mx-4">
              <p className="text-muted-foreground">Loading AI tools...</p>
            </Card>
          ) : filteredTools.length > 0 ? (
            <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4">
              {filteredTools.map((tool) => (
                <Card key={tool.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      {tool.logo_url && (
                        <img 
                          src={tool.logo_url} 
                          alt={`${tool.name} logo`}
                          className="w-12 h-12 object-contain rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <CardTitle className="text-lg">{tool.name}</CardTitle>
                      </div>
                    </div>
                    <CardDescription>{tool.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tool.use_case_tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <Button asChild className="w-full">
                      <a href={tool.external_link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Visit Tool
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            ) : (
            <Card className="p-6 md:p-8 text-center mx-4">
              <p className="text-muted-foreground">
                {tools.length === 0 
                  ? "No AI tools available yet. Check back soon!" 
                  : "No tools match your filters. Try adjusting your search."}
              </p>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AIToolsPage;